<template>
    <div :class="[outerClass, colSize]">
        <label v-if="label"
               class="block"
               :class="{ 'required-label': hasRequiredAttr, [labelClass]: true }"
               :style="labelStyle">{{
                __(label)
            }}</label>
        <Editor class="w-full"
                ref="editorRef"
                :editorStyle="editorStyle"
                :readonly="isFormDisabled"
                :name="fieldName"
                :validation="validation"
                :modules="editorToolbar[toolbar]"
                @load="onEditorLoad"
                v-focus="isFocus"
                v-model="field"
                :unstyled="false"
                :pt="editorPt">
            <template #toolbar>
                <span class="ql-formats">
                </span>
            </template>
        </Editor>
        <span :name="fieldName"></span>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import Editor from 'primevue/editor';
import { __ } from "@helper/utils";

const props = defineProps({
    fieldName: {
        type: String,
        required: true,
    },
    validation: {
        type: [Array, String],
        default: null,
    },
    label: {
        type: String,
        default: null,
    },
    size: {
        type: String,
        default: null
    },
    isFocus: {
        type: Boolean,
        default: false
    },
    isFormDisabled: {
        type: Boolean,
        default: false
    },
    editorStyle: {
        type: Object,
        default: { height: "120px" },
    },
    toolbar: {
        type: String,
        default: 'small',
    },
    labelClass: {
        type: String,
        default: null
    },
    labelStyle: {
        type: [Object, String],
        default: null
    },
    inRepeater: {
        type: Boolean,
        default: false
    },
})

const field = defineModel('field');
const validation = ref(Array.isArray(props.validation) ? props.validation : [props.validation]);
const colSize = props?.size ? 'col-' + props.size : 'col';
const hasRequiredAttr = validation.value.includes('required');
const outerClass = ref('field');

const editorPt = {
    root: {
        name: props.fieldName,
        class: ['omnia-editor', hasRequiredAttr ? 'required-field' : '']
    },
    toolbar: {
        class: 'omnia-editor__toolbar'
    },
    content: {
        class: ['omnia-editor__content', props.validation == 'required' ? 'required-field' : ''],
    }
}

if (props.inRepeater) {
    outerClass.value = null;
}

const editorRef = ref(null);
const toolbarNone = ref({
    toolbar: null
});
const toolbarSmall = ref({
    toolbar: [
        ['bold', 'italic', 'underline', 'strike', { 'color': [] }],
        [{ 'align': [] }, { 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        ['clean',],
    ]
});
const toolbarMedium = ref({
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        ['link', 'image'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }, { 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        ['clean',],
    ]
});
const toolbarLarge = ref({
    toolbar: [
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        ['link', 'image'],
        [{ 'color': [] }, { 'background': [] }, { 'font': [] }],
        [{ 'align': [] }, { 'indent': '+1' }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'list': 'check' }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['clean',],
    ]
});

const editorToolbar = ref({
    none: toolbarNone.value,
    small: toolbarSmall.value,
    medium: toolbarMedium.value,
    large: toolbarLarge.value,
});

const tooltipsSmall = [__('Bold'), __('Italic'), __('Underline'), __('Strikethrough'), __('Font color'), __('Text alignment'), __('List ordered'), __('List bullet'), __('List check'), __('Clean')]
const tooltipsMedium = [__('Bold'), __('Italic'), __('Underline'), __('Strikethrough'), __('Subscript'), __('Superscript'), __('Link'), __('Image'), __('Font color'), __('Background color'), __('Text alignment'), __('List ordered'), __('List bullet'), __('List check'), __('Clean')]
const tooltipsLarge = [__('Bold'), __('Italic'), __('Underline'), __('Strikethrough'), __('Subscript'), __('Superscript'), __('Link'), __('Image'), __('Font color'), __('Background color'), __('Font type'), __('Text alignment'), __('Text indent'), __('List ordered'), __('List bullet'), __('List check'), __('Font size'), __('Header size'), __('Clean')]
const tooltips = { small: tooltipsSmall, medium: tooltipsMedium, large: tooltipsLarge };

const tmpRef = ref(false);

//------------------------------------------------------------------------------------------------------------------------

watch(() => tmpRef, (newValue, oldValue) => {
    if (newValue.value) {
        const toolbar = editorRef.value?.$el?.querySelector('.ql-toolbar')
        const buttons = toolbar.querySelectorAll('button, select');
        buttons.forEach((button, index) => {
            button.setAttribute('title', tooltips[props.toolbar][index]);
            if (button.tagName === 'SELECT') {
                button.previousElementSibling?.setAttribute('title', tooltips[props.toolbar][index]);
            }
        });
    }
}, { deep: true, immediate: false })

//------------------------------------------------------------------------------------------------------------------------

const onEditorLoad = (() => {
    const toolbar = editorRef.value.$el
    if (toolbar) {
        tmpRef.value = true;
    }

});
</script>

<style lang="scss" scoped>
:deep(.omnia-editor) {
    width: 100%;
}

:deep(.omnia-editor__content) {
    overflow: auto;
}

:deep(.omnia-editor) {
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
    background: #ffffff;
    overflow: hidden;
}

:deep(.omnia-editor__toolbar) {
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
}

:deep(.omnia-editor .ql-toolbar),
:deep(.omnia-editor .ql-container) {
    border-color: #d1d5db;
}

:deep(.omnia-editor .ql-toolbar.ql-snow) {
    border-top-left-radius: 0.375rem;
    border-top-right-radius: 0.375rem;
}

:deep(.omnia-editor .ql-container.ql-snow) {
    border-bottom-left-radius: 0.375rem;
    border-bottom-right-radius: 0.375rem;
}

:deep(.omnia-editor .ql-stroke) {
    stroke: currentColor;
}

:deep(.omnia-editor .ql-fill) {
    fill: currentColor;
}

:deep(.omnia-editor .ql-toolbar button:hover .ql-stroke),
:deep(.omnia-editor .ql-toolbar button:focus .ql-stroke),
:deep(.omnia-editor .ql-toolbar button.ql-active .ql-stroke),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:hover .ql-stroke),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:focus .ql-stroke) {
    stroke: #111827 !important;
    opacity: 1;
}

:deep(.omnia-editor .ql-toolbar button:hover .ql-fill),
:deep(.omnia-editor .ql-toolbar button:focus .ql-fill),
:deep(.omnia-editor .ql-toolbar button.ql-active .ql-fill),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:hover .ql-fill),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:focus .ql-fill) {
    fill: #111827 !important;
    opacity: 1;
}

:deep(.omnia-editor .ql-toolbar button),
:deep(.omnia-editor .ql-toolbar .ql-picker-label) {
    color: #374151;
}

:deep(.omnia-editor .ql-toolbar button) {
    border-radius: 0.375rem;
}

:deep(.omnia-editor .ql-toolbar button:hover),
:deep(.omnia-editor .ql-toolbar button:focus),
:deep(.omnia-editor .ql-toolbar button.ql-active),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:hover),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:focus) {
    color: #111827;
}

:deep(.omnia-editor .ql-toolbar button:hover),
:deep(.omnia-editor .ql-toolbar button:focus),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:hover),
:deep(.omnia-editor .ql-toolbar .ql-picker-label:focus) {
    background: #f3f4f6;
}

:deep(.omnia-editor .ql-toolbar button.ql-active) {
    background: #e5e7eb;
}

:deep(.omnia-editor:focus-within .ql-toolbar),
:deep(.omnia-editor:focus-within .ql-container) {
    border-color: rgba(59, 130, 246, 1);
}

:deep(.omnia-editor:focus-within) {
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25);
    border-radius: 0.375rem;
}
</style>
